'use client'

import { IChildrenProps } from '@/utils/typings/children-props'
import { createContext, useCallback, useContext, useEffect, useId, useState } from 'react'
import { IDynamicIslandItemProps } from '@/features/dynamic-island/dynamic-island-item'
import { noOp } from '@/utils/functions/no-op'
import { compact } from '@/utils/functions/compact'
import { DynamicIsland } from '@/features/dynamic-island/dynamic-island'
import { setDynamicIslandActive } from '@/features/dynamic-island/dynamic-island-registry'

type IDynamicIslandStatePipelineItem<T extends string = string> = IDynamicIslandItemProps<T> & {
    id: string
    priority: number
}

type IDynamicIslandContextSetState = <T extends string = string>(
    meta: { id: string },
    setState: (prevState: IDynamicIslandStatePipelineItem<T> | null) => IDynamicIslandStatePipelineItem<T> | null
) => void

interface IDynamicIslandContext {
    contextId: string
    states: IDynamicIslandStatePipelineItem[]
    setState: IDynamicIslandContextSetState
    clearFromState: (id: string) => void
}

const DynamicIslandContext = createContext<IDynamicIslandContext>({
    contextId: '',
    states: [],
    setState: noOp,
    clearFromState: noOp,
})

interface IDynamicIslandContextProviderProps extends IChildrenProps {
    yieldsToOthers?: boolean
}

export const DynamicIslandContextProvider = ({ children, yieldsToOthers }: IDynamicIslandContextProviderProps) => {
    const contextId = useId()
    const [states, setStates] = useState<IDynamicIslandStatePipelineItem[]>([])

    const hasItems = states.length > 0

    useEffect(() => {
        setDynamicIslandActive(contextId, hasItems)
        return () => setDynamicIslandActive(contextId, false)
    }, [contextId, hasItems])

    const setState = useCallback<IDynamicIslandContextSetState>(
        (meta, setState) => {
            // @ts-expect-error
            setStates((prev) => {
                const targetPrevIndex = prev.findIndex((item) => item.id === meta.id) ?? -1

                const targetPrev = targetPrevIndex > -1 ? prev[targetPrevIndex] : null

                // @ts-expect-error
                const nextStateValue = setState(targetPrev)

                if (!targetPrev && nextStateValue?.state === 'idle') {
                    return prev
                }

                const rawNextState =
                    targetPrevIndex === -1
                        ? [...prev, nextStateValue]
                        : // @ts-expect-error
                          prev.reduce((acc, item, index) => {
                              if (index === targetPrevIndex) return [...acc, nextStateValue]

                              return [...acc, item]
                          }, [])

                // @ts-expect-error
                return compact(rawNextState)
            })
        },
        [setStates]
    )

    const clearFromState = useCallback((id: string) => {
        setStates((prev) => prev.filter((item) => item.id !== id))
    }, [])

    return (
        <DynamicIslandContext.Provider value={{ contextId, states, setState, clearFromState }}>
            <DynamicIsland yieldsToOthers={yieldsToOthers} />
            {children}
        </DynamicIslandContext.Provider>
    )
}

export const useDynamicIslandContext = () => useContext(DynamicIslandContext)
